/**
 * Keeps an already-open tab's email-verification state fresh.
 *
 * Confirming the signup email happens on `/verify-email`, which the email
 * client opens as a SEPARATE tab. Every tab the user already had open keeps the
 * `useAuth()` user it hydrated at load, so `emailConfirmedAt` stays null there
 * and the Settings panel keeps showing "Unverified" (plus the resend button)
 * until a manual reload — which is exactly the state the user just fixed.
 *
 * Two signals, both cheap, and both active ONLY while the signed-in user is
 * still unverified (once `emailConfirmedAt` is set the effect unsubscribes and
 * this costs nothing):
 *  - a {@link onAuthBroadcast} message from the confirming tab — instant, same
 *    browser;
 *  - a window focus / tab-visible refresh — covers confirming in a different
 *    browser or on a phone, and any browser without BroadcastChannel.
 *
 * Both funnel into `useAuth().refresh()`, which for this app's cookie-session
 * config re-fetches `/users/me` — so the SERVER decides whether the email is
 * confirmed. A focus refresh is throttled so tabbing back and forth cannot turn
 * into a request loop.
 *
 * @module
 */

import { useEffect, useRef } from 'react'

import { useAuth } from '@molecule/app-react'

import { onAuthBroadcast } from '../utilities/authBroadcast.js'

/** Minimum gap between `/users/me` refreshes, whichever signal asked for one. */
const REFRESH_THROTTLE_MS = 3000

/**
 * Re-derives the session whenever another tab confirms the email, or this tab
 * regains focus, while the signed-in user is still unverified.
 */
export function useEmailVerificationSync(): void {
  const { isAuthenticated, user, refresh } = useAuth()
  // molecule-dev's verification truth is emailConfirmedAt (same cast as the
  // Settings panel — it is a molecule-dev users column, not part of the shared
  // UserProfile type).
  const verified = !!(user as { emailConfirmedAt?: string | null } | null)?.emailConfirmedAt
  const lastRefreshRef = useRef(0)

  useEffect(() => {
    if (!isAuthenticated || verified) return

    let cancelled = false
    // Throttled across BOTH signals: the confirming tab's broadcast lands at
    // about the same moment as the focus that follows it, and they would fetch
    // the identical thing.
    const doRefresh = (): void => {
      if (cancelled) return
      if (Date.now() - lastRefreshRef.current < REFRESH_THROTTLE_MS) return
      lastRefreshRef.current = Date.now()
      // A failed probe leaves the current user state as-is; the next focus
      // retries. Never log the user out from here.
      void refresh().catch(() => {})
    }

    const onFocus = (): void => {
      if (document.visibilityState === 'hidden') return
      doRefresh()
    }

    const unsubscribe = onAuthBroadcast(() => doRefresh())
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)

    return () => {
      cancelled = true
      unsubscribe()
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [isAuthenticated, verified, refresh])
}

/**
 * Mount-once host for {@link useEmailVerificationSync}. Renders nothing.
 *
 * @returns `null`.
 */
export function EmailVerificationSync(): null {
  useEmailVerificationSync()
  return null
}
